import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { loaderDecrement, loaderIncrement } from "../services/globalLoader";

const SORT_FIELDS = [
  { key: "name", label: "Name" },
  { key: "email", label: "Email" },
  { key: "phone", label: "Mobile" },
  { key: "block", label: "Block" },
  { key: "flatNumber", label: "Flat no." },
  { key: "bhkType", label: "BHK" },
  { key: "squareFeet", label: "Sq.ft" }
];

const formatNumber = (n) => new Intl.NumberFormat("en-IN").format(Number(n || 0));

const statusLabel = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "—";
  if (raw.toLowerCase().replace(/[\s_-]/g, "") === "waitingforapproval") return "Waiting for approval";
  return raw;
};

const PropertyApproval = () => {
  const navigate = useNavigate();
  const API_BASE_URL = process.env.REACT_APP_API_URL;
  const userid = useMemo(() => {
    try {
      return String(localStorage.getItem("userid") || "").trim();
    } catch {
      return "";
    }
  }, []);

  const [owners, setOwners] = useState([]);
  const [ready, setReady] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("updatedAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 1
  });

  const fetchOwners = useCallback(
    async ({ page = 1, limit = 10, search = "", nextSortBy = "updatedAt", nextSortOrder = "desc" } = {}) => {
      if (!API_BASE_URL) {
        toast.error("Missing REACT_APP_API_URL in environment");
        setReady(true);
        return;
      }
      if (!userid) {
        toast.error("Missing userid. Please login again.");
        setReady(true);
        return;
      }

      const safeLimit = Math.min(100, Math.max(1, Number(limit) || 10));
      let didIncrement = false;
      try {
        const payload = {
          action: "admin.getWaitingApprovals",
          userid,
          page,
          limit: safeLimit,
          sortBy: nextSortBy,
          sortOrder: nextSortOrder
        };
        if (String(search || "").trim()) payload.search = String(search).trim();

        loaderIncrement();
        didIncrement = true;
        const response = await axios.post(`${API_BASE_URL}`, payload);
        const data = response?.data;
        if (data?.status === "success" || data?.success === true) {
          const list = Array.isArray(data?.owners) ? data.owners : [];
          setOwners(list);
          setPagination(
            data.pagination || {
              page,
              limit: safeLimit,
              total: list.length,
              pages: 1
            }
          );
        } else {
          toast.error(data?.message || "Failed to load waiting approvals");
          setOwners([]);
        }
      } catch (error) {
        toast.error(error?.response?.data?.message || error?.message || "Something went wrong");
        setOwners([]);
      } finally {
        if (didIncrement) loaderDecrement();
        setReady(true);
      }
    },
    [API_BASE_URL, userid]
  );

  useEffect(() => {
    fetchOwners({ page: 1, limit: 10, search: "", nextSortBy: "updatedAt", nextSortOrder: "desc" });
  }, [fetchOwners]);

  const applySearch = (e) => {
    e.preventDefault();
    setSearchQuery(searchInput);
    setPagination((prev) => ({ ...prev, page: 1 }));
    fetchOwners({
      page: 1,
      limit: pagination.limit,
      search: searchInput,
      nextSortBy: sortBy,
      nextSortOrder: sortOrder
    });
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchInput(value);
    if (!value.trim() && searchQuery) {
      setSearchQuery("");
      setPagination((prev) => ({ ...prev, page: 1 }));
      fetchOwners({
        page: 1,
        limit: pagination.limit,
        search: "",
        nextSortBy: sortBy,
        nextSortOrder: sortOrder
      });
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > pagination.pages || newPage === pagination.page) return;
    setPagination((prev) => ({ ...prev, page: newPage }));
    fetchOwners({
      page: newPage,
      limit: pagination.limit,
      search: searchQuery,
      nextSortBy: sortBy,
      nextSortOrder: sortOrder
    });
  };

  const handleLimitChange = (newLimit) => {
    const limit = Math.min(100, Number(newLimit) || 10);
    setPagination((prev) => ({ ...prev, page: 1, limit }));
    fetchOwners({
      page: 1,
      limit,
      search: searchQuery,
      nextSortBy: sortBy,
      nextSortOrder: sortOrder
    });
  };

  const handleSort = (field) => {
    const nextOrder = sortBy === field && sortOrder === "desc" ? "asc" : "desc";
    setSortBy(field);
    setSortOrder(nextOrder);
    setPagination((prev) => ({ ...prev, page: 1 }));
    fetchOwners({
      page: 1,
      limit: pagination.limit,
      search: searchQuery,
      nextSortBy: field,
      nextSortOrder: nextOrder
    });
  };

  const generatePageNumbers = () => {
    const current = pagination.page;
    const total = pagination.pages || 1;
    const delta = 2;
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

    const pages = [1];
    let start = Math.max(2, current - delta);
    let end = Math.min(total - 1, current + delta);
    if (current <= delta + 2) end = Math.min(total - 1, 5);
    if (current >= total - delta - 1) start = Math.max(2, total - 4);
    if (start > 2) pages.push("...");
    for (let i = start; i <= end; i += 1) pages.push(i);
    if (end < total - 1) pages.push("...");
    if (total > 1) pages.push(total);
    return pages;
  };

  const pageNumbers = generatePageNumbers();
  const totalOwners = pagination.total || owners.length;

  const sortMark = (field) => {
    if (sortBy !== field) return "";
    return sortOrder === "asc" ? " ↑" : " ↓";
  };

  const handleView = (owner) => {
    const id = owner?.id;
    if (!id) {
      toast.error("Owner id missing for this row.");
      return;
    }
    navigate(`/property-details-view/${encodeURIComponent(String(id))}`, { state: { owner } });
  };

  return (
    <div className="employee-form">
      <div className="content-header property-approval-header">
        <div className="container-fluid">
          <h1 className="m-0 txt_lft">Property approval</h1>
          <p>Owners waiting for property approval</p>
        </div>
      </div>

      <div className="content">
        <div className="container-fluid">
          <div className="card">
            <div className="card-body">
              <div className="role-table-box">
                <div className="role-table-top">
                  <form onSubmit={applySearch} className="role-table-search-wrap">
                    <input
                      type="text"
                      className="form-control role-table-search"
                      placeholder="Search name, email, phone, flat, block, or BHK"
                      value={searchInput}
                      onChange={handleSearchChange}
                    />
                  </form>
                </div>

                <div className="table-responsive">
                  <table className="table table-hover role-table">
                    <thead>
                      <tr>
                        <th style={{ width: 60 }}>
                          <span className="role-th">Sl no.</span>
                        </th>
                        {SORT_FIELDS.map((field) => (
                          <th key={field.key}>
                            <button
                              type="button"
                              className="role-th"
                              onClick={() => handleSort(field.key)}
                              style={{
                                background: "transparent",
                                border: 0,
                                padding: 0,
                                fontWeight: 600,
                                cursor: "pointer"
                              }}
                            >
                              {field.label}
                              {sortMark(field.key)}
                            </button>
                          </th>
                        ))}
                        <th>
                          <span className="role-th">Property type</span>
                        </th>
                        <th>
                          <span className="role-th">Property nature</span>
                        </th>
                        <th>
                          <span className="role-th">Status</span>
                        </th>
                        <th style={{ width: 90 }}>
                          <span className="role-th">Action</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {owners.length ? (
                        owners.map((owner, idx) => (
                          <tr key={owner?.id || idx}>
                            <td>{(pagination.page - 1) * pagination.limit + idx + 1}</td>
                            <td>{owner?.name || "—"}</td>
                            <td>{owner?.email || "—"}</td>
                            <td>{owner?.phone || "—"}</td>
                            <td>{owner?.block || "—"}</td>
                            <td>{owner?.flatNumber || "—"}</td>
                            <td>{owner?.bhkType || "—"}</td>
                            <td>{owner?.squareFeet == null || owner?.squareFeet === "" ? "—" : formatNumber(owner.squareFeet)}</td>
                            <td>{owner?.propertyType || "—"}</td>
                            <td>{owner?.propertyNature || "—"}</td>
                            <td>
                              <span
                                className="badge"
                                style={{
                                  padding: "0.35rem 0.65rem",
                                  borderRadius: 999,
                                  fontWeight: 600,
                                  fontSize: 11,
                                  background: "#e7f6f8",
                                  color: "#0d5c66"
                                }}
                              >
                                {statusLabel(owner?.propertyInfo)}
                              </span>
                            </td>
                            <td>
                              <button
                                type="button"
                                className="role-action-btn role-action-view"
                                title="View"
                                onClick={() => handleView(owner)}
                              >
                                <i className="fa-solid fa-eye" />
                              </button>
                            </td>
                          </tr>
                        ))
                      ) : ready ? (
                        <tr>
                          <td colSpan={12} className="text-center text-muted">
                            {searchQuery
                              ? "No owners found matching your search"
                              : "No owners waiting for approval"}
                          </td>
                        </tr>
                      ) : null}
                    </tbody>
                  </table>
                </div>

                <div className="role-table-footer">
                  <div className="d-flex justify-content-between align-items-center flex-wrap" style={{ gap: 10 }}>
                    <div className="d-flex align-items-center" style={{ gap: 10 }}>
                      <span className="text-muted" style={{ fontSize: 12 }}>
                        Show:
                      </span>
                      <select
                        className="form-control"
                        style={{ width: 90 }}
                        value={pagination.limit}
                        onChange={(e) => handleLimitChange(Number(e.target.value))}
                      >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                      <span className="text-muted" style={{ fontSize: 12 }}>
                        of {formatNumber(totalOwners)} entries
                      </span>
                    </div>

                    <div className="d-flex align-items-center" style={{ gap: 4 }}>
                      <button
                        type="button"
                        className="btn btn-light"
                        disabled={pagination.page <= 1}
                        onClick={() => handlePageChange(1)}
                        style={{ minWidth: 32, height: 32, padding: "4px 8px", fontSize: 12 }}
                      >
                        &laquo;
                      </button>
                      <button
                        type="button"
                        className="btn btn-light"
                        disabled={pagination.page <= 1}
                        onClick={() => handlePageChange(pagination.page - 1)}
                        style={{ minWidth: 32, height: 32, padding: "4px 8px", fontSize: 12 }}
                      >
                        &lsaquo;
                      </button>
                      {pageNumbers.map((pageNum, index) => {
                        if (pageNum === "...") {
                          return (
                            <span
                              key={`ellipsis-${index}`}
                              style={{
                                padding: "4px 8px",
                                fontSize: 12,
                                color: "#6c757d",
                                minWidth: 32,
                                textAlign: "center",
                                display: "inline-block"
                              }}
                            >
                              ...
                            </span>
                          );
                        }
                        const isActive = pageNum === pagination.page;
                        return (
                          <button
                            key={pageNum}
                            type="button"
                            className={`btn ${isActive ? "btn-primary" : "btn-light"}`}
                            onClick={() => handlePageChange(pageNum)}
                            style={{
                              minWidth: 32,
                              height: 32,
                              padding: "4px 8px",
                              fontSize: 12,
                              fontWeight: isActive ? 600 : 400
                            }}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                      <button
                        type="button"
                        className="btn btn-light"
                        disabled={pagination.page >= pagination.pages}
                        onClick={() => handlePageChange(pagination.page + 1)}
                        style={{ minWidth: 32, height: 32, padding: "4px 8px", fontSize: 12 }}
                      >
                        &rsaquo;
                      </button>
                      <button
                        type="button"
                        className="btn btn-light"
                        disabled={pagination.page >= pagination.pages}
                        onClick={() => handlePageChange(pagination.pages)}
                        style={{ minWidth: 32, height: 32, padding: "4px 8px", fontSize: 12 }}
                      >
                        &raquo;
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PropertyApproval;
